import { useEffect, useRef, useState } from 'react';
import useCrmInteraction from 'api/hooks/useCrmInteraction';

const DEFAULT_PAGE_SIZE = 10;

// /api/crm-interaction/query's hits have no `type` field (the indexed CrmInteraction model doesn't
// carry one - it's only ever needed where multiple content types mix together, e.g. reports).
// Consumers that select these items for a report (MainListActionsBar.jsx, AddUpdatesToReport.jsx)
// key off `item.type === 'CrmInteraction'` to detect that, matching what report-widget code paths
// already attach by hand - tag it once here so every consumer gets a consistent shape.
// Same story for `objectID`: this DTO (PolicyMogul.Models.Crm.CrmInteraction, the Get/Create/Update/
// query response) has no such field, unlike the ES-indexed model other content types return - so
// SaveItemPopup's objectId(item) fell through to `item.objectID ?? item.objectId`, got undefined,
// and silently saved the item with no way to look it back up. Synthesized here to match exactly what
// the backend's own IndexableContentObjectIdGenerator.GetObjectId(CrmInteraction) produces.
const tagInteractionType = (hits) =>
  (hits ?? []).map((hit) => ({ type: 'CrmInteraction', objectID: `CrmInteraction-${hit.id}`, ...hit }));

const useCrmInteractionsQuery = ({
  filters,
  sort,
  query,
  pageSize = DEFAULT_PAGE_SIZE,
  dependencyKey,
  enabled = true,
  cachedResult,
  skipInitialFetch,
  onResultChange,
}) => {
  const hasUsableCache = skipInitialFetch && cachedResult?.hits?.length > 0;
  const [isLoading, setIsLoading] = useState(!hasUsableCache);
  const [interactions, setInteractions] = useState(hasUsableCache ? tagInteractionType(cachedResult.hits) : null);
  const [thereIsMoreContent, setThereIsMoreContent] = useState(hasUsableCache ? cachedResult.thereIsMoreContent : false);
  const [isLoadingScroll, setIsLoadingScroll] = useState(false);

  const { queryCrmInteractions } = useCrmInteraction();

  const pageRef = useRef(hasUsableCache ? Math.max(1, Math.ceil((cachedResult.hits?.length ?? 0) / pageSize)) : 1);
  // Lets the very first effect run below skip its fetch when there's a cache to seed from - consumed
  // (set back to false) inside that same effect once used, so it only ever skips that one time.
  // skipInitialFetch (the prop this is seeded from) never flips back on its own, so without resetting
  // it here every later dependencyKey change - a real filter/search edit - kept hitting the same
  // early return instead of ever fetching again.
  const hasUsableCacheRef = useRef(hasUsableCache);
  const loadMoreSourceRef = useRef(null);
  const initialCall = useRef(null);
  const loadMoreCall = useRef(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const queryRef = useRef(query);
  queryRef.current = query;

  initialCall.current = async (signal) => {
    setIsLoading(true);
    const requestProperties = { signal, pageNumber: 1, pageSize };
    if (sort) requestProperties.sort = sort;
    if (filtersRef.current?.length) requestProperties.filters = filtersRef.current;
    if (queryRef.current) requestProperties.query = queryRef.current;
    try {
      const result = await queryCrmInteractions(requestProperties);
      if (signal.aborted) return;
      if (result) {
        setInteractions(tagInteractionType(result.hits));
        setThereIsMoreContent((result.hits ?? []).length === pageSize);
      }
    } catch (error) {}
    setIsLoading(false);
  };

  loadMoreCall.current = async (signal, pageNumber) => {
    setIsLoadingScroll(true);
    const requestProperties = { signal, pageNumber, pageSize };
    if (sort) requestProperties.sort = sort;
    if (filtersRef.current?.length) requestProperties.filters = filtersRef.current;
    if (queryRef.current) requestProperties.query = queryRef.current;
    try {
      const result = await queryCrmInteractions(requestProperties);
      if (signal.aborted) return;
      if (result) {
        setInteractions((prev) => [...(prev ?? []), ...tagInteractionType(result.hits)]);
        setThereIsMoreContent((result.hits ?? []).length === pageSize);
      }
    } catch (error) {}
    setIsLoadingScroll(false);
  };

  const handleScrollBottom = () => {
    if (isLoadingScroll || !thereIsMoreContent) return;
    loadMoreSourceRef.current?.abort();
    loadMoreSourceRef.current = new AbortController();
    pageRef.current += 1;
    loadMoreCall.current(loadMoreSourceRef.current.signal, pageRef.current);
  };

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setInteractions([]);
      setThereIsMoreContent(false);
      return;
    }
    if (hasUsableCacheRef.current) {
      // Consumed - only the very first run (mount) skips its fetch this way. Without resetting it,
      // it stayed true forever and every later dependencyKey change (a real filter/search edit) kept
      // hitting this same early return instead of ever fetching again.
      hasUsableCacheRef.current = false;
      setIsLoading(false);
      return;
    }
    const source = new AbortController();
    loadMoreSourceRef.current?.abort();
    pageRef.current = 1;
    initialCall.current(source.signal);
    return () => {
      source.abort();
      loadMoreSourceRef.current?.abort();
    };
  }, [dependencyKey, enabled]);

  useEffect(() => {
    if (interactions !== null) onResultChange?.({ hits: interactions, thereIsMoreContent, dependencyKey });
  }, [interactions, thereIsMoreContent]);

  const addInteraction = (newInteraction) => {
    setInteractions((prev) => [newInteraction, ...(prev ?? [])]);
  };

  const updateInteractionLocal = (updatedInteraction) => {
    setInteractions((prev) =>
      (prev ?? []).map((item) => (item.id === updatedInteraction.id ? { ...item, ...updatedInteraction } : item))
    );
  };

  const removeInteraction = (id) => {
    setInteractions((prev) => (prev ?? []).filter((item) => item.id !== id));
  };

  const refetch = () => {
    loadMoreSourceRef.current?.abort();
    pageRef.current = 1;
    initialCall.current(new AbortController().signal);
  };

  return {
    interactions,
    isLoading,
    isLoadingScroll,
    thereIsMoreContent,
    handleScrollBottom,
    addInteraction,
    updateInteractionLocal,
    removeInteraction,
    refetch,
  };
};

export default useCrmInteractionsQuery;
